import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ActiveQRCodesList = ({ qrCodes, onDeactivate, loading, isStaffMember }) => {
  const [selectedQR, setSelectedQR] = useState(null);

  const getExpiryStatus = (expiresAt) => {
    if (!expiresAt) return { status: 'never', color: 'text-info', icon: 'Infinity' };
    
    const now = new Date();
    const expiry = new Date(expiresAt);
    const hoursUntilExpiry = (expiry - now) / (1000 * 60 * 60);

    if (expiry <= now) {
      return { status: 'expired', color: 'text-destructive', icon: 'XCircle' };
    } else if (hoursUntilExpiry <= 24) {
      return { status: 'expiring-soon', color: 'text-warning', icon: 'AlertTriangle' };
    } else {
      return { status: 'active', color: 'text-success', icon: 'CheckCircle' };
    }
  };

  const activeQRCodes = qrCodes?.filter(qr => {
    if (!qr?.is_active) return false;
    if (!qr?.expires_at) return true;
    return new Date(qr?.expires_at) > new Date();
  });

  if (loading) {
    return (
      <div className="bg-card rounded-lg p-8 border border-border">
        <div className="text-center py-12">
          <Icon name="Loader" size={48} className="animate-spin mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">Loading QR codes...</p>
        </div>
      </div>
    );
  }

  if (activeQRCodes?.length === 0) {
    return (
      <div className="bg-card rounded-lg p-8 border border-border">
        <div className="text-center py-12">
          <Icon name="QrCode" size={64} className="mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-xl font-bold mb-2">No Active QR Codes</h3>
          <p className="text-muted-foreground">
            Generate a QR code to get started
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* QR Codes List */}
      <div className="lg:col-span-2 space-y-4">
        {activeQRCodes?.map((qr) => {
          const expiryStatus = getExpiryStatus(qr?.expires_at);
          
          return (
            <div
              key={qr?.id}
              className={`bg-card rounded-lg p-6 border cursor-pointer transition-all ${
                selectedQR?.id === qr?.id
                  ? 'border-primary shadow-lg'
                  : 'border-border hover:border-primary/50'
              }`}
              onClick={() => setSelectedQR(qr)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <Icon name="QrCode" size={24} className="text-primary" />
                    <div>
                      <h3 className="font-semibold">
                        {qr?.barcode_data?.startsWith('PROFILE') ? 'Profile QR Code' : 'Member Account QR'}
                      </h3>
                      {isStaffMember && qr?.user_profiles && (
                        <p className="text-sm text-muted-foreground">
                          {qr?.user_profiles?.full_name}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <div>
                      <p className="text-xs text-muted-foreground">Generated</p>
                      <p className="text-sm font-medium">
                        {new Date(qr?.generated_at || qr?.created_at)?.toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Scans</p>
                      <p className="text-sm font-medium">{qr?.scan_count || 0}</p>
                    </div>
                  </div>

                  {qr?.expires_at && (
                    <div className="mb-3">
                      <p className="text-xs text-muted-foreground">Expires</p>
                      <p className="text-sm font-medium">
                        {new Date(qr?.expires_at)?.toLocaleString()}
                      </p>
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                      expiryStatus?.status === 'expired' ?'bg-destructive/10 text-destructive'
                        : expiryStatus?.status === 'expiring-soon' ?'bg-warning/10 text-warning'
                        : expiryStatus?.status === 'never' ?'bg-info/10 text-info' :'bg-success/10 text-success'
                    }`}>
                      <Icon name={expiryStatus?.icon} size={12} />
                      {expiryStatus?.status === 'never' ? 'No Expiry' : 
                       expiryStatus?.status === 'expiring-soon' ? 'Expiring Soon' :
                       expiryStatus?.status === 'expired' ? 'Expired' : 'Active'}
                    </span>
                  </div>
                </div>

                <Button
                  onClick={(e) => {
                    e?.stopPropagation();
                    onDeactivate?.(qr?.id);
                  }}
                  variant="outline"
                  size="sm"
                  iconName="XCircle"
                >
                  Deactivate
                </Button>
              </div>
            </div>
          );
        })}
      </div>
      {/* QR Code Preview */}
      <div className="lg:col-span-1">
        <div className="bg-card rounded-lg p-6 border border-border sticky top-4">
          <h3 className="font-bold mb-4">QR Code Preview</h3>
          
          {selectedQR ? (
            <div className="space-y-4">
              <div className="flex justify-center p-6 bg-white rounded-lg">
                <QRCodeSVG
                  value={selectedQR?.barcode_data}
                  size={200}
                  level="H"
                  includeMargin
                />
              </div>

              <div className="p-3 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">QR Code ID</p>
                <p className="font-mono text-xs break-all">{selectedQR?.barcode_data}</p>
              </div>

              {selectedQR?.last_scanned_at && (
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Last Scanned</p>
                  <p className="text-sm font-medium">
                    {new Date(selectedQR?.last_scanned_at)?.toLocaleString()}
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Button
                  onClick={() => {
                    const svg = document.querySelector('.bg-white svg');
                    const svgData = new XMLSerializer()?.serializeToString(svg);
                    const canvas = document.createElement('canvas');
                    const ctx = canvas?.getContext('2d');
                    const img = new Image();
                    
                    img.onload = () => {
                      canvas.width = img?.width;
                      canvas.height = img?.height;
                      ctx?.drawImage(img, 0, 0);
                      const pngFile = canvas?.toDataURL('image/png');
                      
                      const downloadLink = document.createElement('a');
                      downloadLink.download = `qr-${selectedQR?.barcode_data}.png`;
                      downloadLink.href = pngFile;
                      downloadLink?.click();
                    };
                    
                    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
                  }}
                  variant="outline"
                  iconName="Download"
                  className="w-full"
                >
                  Download
                </Button>
                <Button
                  onClick={() => window.print()}
                  variant="outline"
                  iconName="Printer"
                  className="w-full"
                >
                  Print
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Icon name="QrCode" size={48} className="mx-auto mb-3 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Select a QR code to preview
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActiveQRCodesList;